import './RefreshMetricsModal.scss';
import { useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { MemberMetricsChange } from './creator-sets-constants';
import { formatCount } from './creator-sets-utils';

interface RefreshMetricsModalProps {
  changes: MemberMetricsChange[];
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

// Old vs new metrics for every member, so a refresh never changes a saved set's numbers unseen.
export const RefreshMetricsModal = ({
  changes,
  saving,
  onSave,
  onCancel
}: RefreshMetricsModalProps) => {
  const returned = changes.filter(change => change.next).length;

  const headers = useMemo<TypedHeader<MemberMetricsChange>[]>(
    () => [
      { name: 'displayName', label: 'Creator', flex: 1, nonInteractive: true },
      {
        name: 'subscribers',
        label: 'Subscribers',
        width: 220,
        nonInteractive: true,
        renderer: change => (
          <MetricChange
            returned={Boolean(change.next)}
            previous={change.previous.subscriber_count}
            next={change.next?.subscriber_count}
          />
        )
      },
      {
        name: 'views',
        label: 'Views',
        width: 240,
        nonInteractive: true,
        renderer: change => (
          <MetricChange
            returned={Boolean(change.next)}
            previous={change.previous.views_count}
            next={change.next?.views_count}
          />
        )
      }
    ],
    []
  );

  const renderNoRows = useCallback(
    () => <TableEmptyState message='This set has no creators to refresh.' />,
    []
  );

  return (
    <Modal show centered size='lg' onHide={onCancel} className='refresh-metrics-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Refresh metrics</Modal.Title>
      </Modal.Header>
      <Modal.Body data-testid='refresh-metrics-modal'>
        <div className='refresh-metrics-summary'>
          Google returned current metrics for {returned} of{' '}
          {conditionallyPluralizeWithCount(changes.length, 'creator')}. Creators it didn&apos;t
          return keep their saved metrics. Nothing changes until you save.
        </div>
        <div className='refresh-metrics-table'>
          <BPMTable
            data={changes}
            headers={headers}
            filterBar={false}
            rowHeight={40}
            noRowsRenderer={renderNoRows}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={returned === 0} loading={saving} onClick={onSave}>
          Save refreshed metrics
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface MetricChangeProps {
  // Whether Google returned the creator at all; a returned creator can still lack a count.
  returned: boolean;
  previous?: string;
  next?: string;
}

const MetricChange = ({ returned, previous, next }: MetricChangeProps) => {
  if (!returned) {
    return <span className='metric-change-missing'>{formatCount(previous)} (not returned)</span>;
  }
  return (
    <span className={previous === next ? 'metric-change-same' : 'metric-change-updated'}>
      {formatCount(previous)} → {formatCount(next)}
    </span>
  );
};
